import React, {
    useState,
    useRef,
    useMemo,
    ReactElement,
    useEffect,
} from "react";
import { Select, Spin } from "antd";
import { SelectProps } from "antd/es/select";
import debounce from "lodash/debounce";

type Props = SelectProps & {
    debounceTimeout?: number;
    defaultOptions?: SelectOption[];
    fetchOnInit?: boolean;
    fetchOptions: (
        value?: string,
        includeId?: number | string
    ) => Promise<SelectOption[]>;
    renderOptionFunc?: (option: SelectOption) => ReactElement;
};

const renderFunc = ({ value, label }: SelectOption) => {
    return (
        <Select.Option key={value} value={value}>
            {label}
        </Select.Option>
    );
};

const DebounceSelect = ({
    debounceTimeout = 300,
    defaultOptions = [],
    fetchOnInit = false,
    fetchOptions,
    renderOptionFunc = renderFunc,
    ...props
}: Props) => {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<SelectOption[]>(defaultOptions);
    const fetchRef = useRef(0);

    useEffect(() => {
        if (!fetchOnInit) {
            return;
        }

        fetchOptions(undefined, props.defaultValue ?? props.value).then(
            (newOptions) => {
                setFetching(true);
                setOptions(newOptions);
                setFetching(false);
            }
        );
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [fetchOnInit]);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    // for fetch callback order
                    return;
                }

                setOptions(newOptions);
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    const optionElements = useMemo(
        () => options.map(renderOptionFunc),
        [options, renderOptionFunc]
    );

    return (
        <Select
            className="debounce-select"
            filterOption={false}
            onSearch={debounceFetcher}
            notFoundContent={fetching ? <Spin size="small" /> : null}
            {...props}
        >
            {optionElements}
        </Select>
    );
};

export default DebounceSelect;
